import { useRef, useState } from "react"
import { Camera } from "lucide-react"

import PalmHandLeft from "@/assets/img/palmistry/scan/palm-scan-hand.png"
import PalmHandRight from "@/assets/img/palmistry/scan/palm-scan-hand-right.png"
import { PalmCamera, PalmPhotoResult, useHandLandmarker } from "@/features/palmistry"
import { CameraFlash } from "@/packages/camera"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"

import { QuizHeader } from "../quiz-header"
import type { StepComponentProps } from "./step-contract"

type Phase = "instruction" | "camera" | "flash" | "result"

/**
 * Palm photo-capture quiz step: instruction (within the quiz header/counter) →
 * camera → flash → result-with-lines. Reuses the palmistry capture components.
 * The hand (left/right) is read from the step id. Dumb: advances via onAdvance.
 */
export function PalmCaptureStep({
  step,
  onAdvance,
  onBack,
  countedCurrent,
  countedTotal,
}: StepComponentProps) {
  const hand = step.id.includes("right") ? "right" : "left"
  // Per-hand "photo source picked" event (Trello QZm294ZE), sent on the button
  // tap like the Soulmate selfie step.
  const photoEvent =
    hand === "right"
      ? ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_PHOTO_RIGHT
      : ANALYTICS_EVENTS.SIMPLIFIED_QUIZ_PHOTO_LEFT
  const fileRef = useRef<HTMLInputElement>(null)
  const { landmarker, ready } = useHandLandmarker()
  const [phase, setPhase] = useState<Phase>("instruction")
  const [photo, setPhoto] = useState<string | null>(null)
  const [cameraDenied, setCameraDenied] = useState(false)

  // Warm the image decode so the result screen paints the photo immediately
  // instead of showing the dark base for a few seconds.
  const warmDecode = (dataUrl: string) => {
    const img = new Image()
    img.src = dataUrl
    void img.decode?.().catch(() => {})
  }

  // Camera capture plays the shutter flash before the result.
  const toFlash = (dataUrl: string) => {
    setPhoto(dataUrl)
    warmDecode(dataUrl)
    setPhase("flash")
  }

  // Gallery upload has no shutter, so skip the flash and go straight to result.
  const toResult = (dataUrl: string) => {
    setPhoto(dataUrl)
    warmDecode(dataUrl)
    setPhase("result")
  }

  const startCamera = () => {
    setCameraDenied(false)
    setPhase("camera")
  }

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === "string") toResult(reader.result)
    }
    reader.readAsDataURL(file)
    event.target.value = ""
  }

  if (phase === "camera") {
    return (
      <PalmCamera
        hand={hand}
        onBack={() => setPhase("instruction")}
        onCaptured={toFlash}
        onError={() => {
          setCameraDenied(true)
          setPhase("instruction")
        }}
      />
    )
  }
  if (phase === "flash") {
    return <CameraFlash onComplete={() => setPhase("result")} />
  }
  if (phase === "result") {
    return photo ? (
      <PalmPhotoResult
        imageSrc={photo}
        landmarker={landmarker}
        ready={ready}
        hand={hand}
        onContinue={onAdvance}
        onRetake={() => setPhase("camera")}
        onBack={() => setPhase("camera")}
      />
    ) : null
  }

  return (
    <div
      data-slot="palm-capture-step"
      className="flex h-dvh min-h-dvh flex-col overflow-hidden"
    >
      <QuizHeader current={countedCurrent} total={countedTotal} showBack onBack={onBack} />

      <div className="flex min-h-0 flex-1 flex-col items-center px-4 pt-8 pb-4">
        <h1 className="shrink-0 text-center font-display text-2xl font-medium text-purple-light">
          Take a photo of your {hand} palm
        </h1>

        <div className="flex min-h-0 w-full flex-1 items-center">
          <img
            src={hand === "right" ? PalmHandRight : PalmHandLeft}
            alt=""
            className="h-full max-h-[408px] min-h-0 w-full max-w-[500px] object-cover"
          />
        </div>

        <div className="flex w-full shrink-0 flex-col gap-2">
          {cameraDenied && (
            <p className="text-center font-body text-sm text-peach">
              Camera access is off. Enable it in your settings, or upload a photo from your gallery.
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              trackEvent(photoEvent, { metadata: { source: "take_photo" } })
              startCamera()
            }}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            <Camera className="size-6" />
            Take a photo
          </button>

          <button
            type="button"
            onClick={() => {
              trackEvent(photoEvent, { metadata: { source: "gallery" } })
              fileRef.current?.click()
            }}
            className="flex h-14 w-full items-center justify-center rounded-full bg-purple-light/10 font-body text-base font-semibold text-purple-light"
          >
            Upload from gallery
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/png, image/jpeg"
            onChange={handleFile}
            className="hidden"
          />
        </div>
      </div>
    </div>
  )
}
